import type { ExpressionSpecification } from 'mapbox-gl';
import { FONT_SIZE_100 } from '@alltrails/denali/tokens';
import { getTrailheadPinStyle, type TrailheadPinStyle } from './trailheadPinVariant';

/**
 * https://www.figma.com/design/Hfw8SV8IylHR4A3nnQ7Fvs/Map-Icons?node-id=481-1698&p=f&m=dev
 * Trailhead pins draw their art from the Mapbox style sprite (not SVGs created at runtime).
 * This helper creates the correct string that matches the name of the svg in the sprite (ex: 'trailhead-circle-cluster-closed').
 *
 * The sprite has the same art under two prefixes, `trailhead-` and `guidepost-`, and the pin style picks one. The collapsed
 * dots only come as `trailhead-dot-*`, so they look the same whichever prefix the pins use.
 *
 * A trailhead with one trail does not have a count banner. Trailheads with two or
 * more trails use the trailhead icon with a count banner, which come in three widths, one per digit
 * of the trail count. The count banner is drawn by the layer's `text-field` because an icon cannot
 * render feature data. Collapsed and selected pins are fixed-size icons with no count.
 */

export type TrailheadPinVariant = 'closed' | 'saved' | 'completed' | 'default';

export type TrailheadVariantExpressions = {
  isClosedExpression: ExpressionSpecification;
  isCompletedExpression: ExpressionSpecification;
  isSavedExpression: ExpressionSpecification;
};

const defaultPinStyle = getTrailheadPinStyle();

// Negative is up, so the icon sits above the trailhead it marks
const trailheadIconVerticalOffset = -14;

const imageName = ({ iconPrefix }: TrailheadPinStyle, name: string) => `${iconPrefix}-${name}`;

// One name per digit count: under 10, under 100, and everything larger.
const clusterImageNames: Record<TrailheadPinVariant, [string, string, string]> = {
  closed: ['circle-cluster-closed', 'circle-cluster-closed2', 'circle-cluster-closed3'],
  saved: ['circle-cluster-favorite', 'circle-cluster-favorite2', 'circle-cluster-favorite3'],
  completed: ['circle-cluster-completed', 'circle-cluster-completed2', 'circle-cluster-completed3'],
  default: ['circle-cluster', 'circle-cluster2', 'circle-cluster3']
};

const singleTrailImageNames: Record<TrailheadPinVariant, string> = {
  closed: 'circle-closed',
  saved: 'circle-favorite',
  completed: 'circle-completed',
  default: 'circle-default'
};

const selectedImageNames: Record<TrailheadPinVariant, string> = {
  closed: 'selected-closed',
  saved: 'selected-favorite',
  completed: 'selected-completed',
  default: 'selected'
};

// Not prefixed: the same dots draw under every pin style
const collapsedImageNames: Record<TrailheadPinVariant, string> = {
  closed: 'trailhead-dot-minus',
  saved: 'trailhead-dot-heart',
  completed: 'trailhead-dot-check',
  default: 'trailhead-dot-default'
};

/**
 * Closed wins over saved, and saved over completed, so a trailhead whose trails are all closed
 * reads as closed no matter what else is true of it. This matches how the trail pins rank the same
 * three states.
 */
export function buildVariantExpression(
  { isClosedExpression, isCompletedExpression, isSavedExpression }: TrailheadVariantExpressions,
  imageForVariant: (variant: TrailheadPinVariant) => string | ExpressionSpecification
): ExpressionSpecification {
  return [
    'case',
    isClosedExpression,
    imageForVariant('closed'),
    isSavedExpression,
    imageForVariant('saved'),
    isCompletedExpression,
    imageForVariant('completed'),
    imageForVariant('default')
  ];
}

function buildClusterImageExpression(
  pinStyle: TrailheadPinStyle,
  variant: TrailheadPinVariant,
  trailCountExpression: ExpressionSpecification
): ExpressionSpecification {
  const [oneDigit, twoDigit, threeDigit] = clusterImageNames[variant];

  return [
    'case',
    ['==', trailCountExpression, 1],
    imageName(pinStyle, singleTrailImageNames[variant]),
    ['<', trailCountExpression, 10],
    imageName(pinStyle, oneDigit),
    ['<', trailCountExpression, 100],
    imageName(pinStyle, twoDigit),
    imageName(pinStyle, threeDigit)
  ];
}

/** Expanded pins: the circle cluster sized to the count (or the label pill), or the selected pin when active. */
export function buildTrailheadIconImage(
  variantExpressions: TrailheadVariantExpressions,
  isActiveExpression: ExpressionSpecification,
  trailCountExpression: ExpressionSpecification,
  pinStyle: TrailheadPinStyle = defaultPinStyle
): ExpressionSpecification {
  return [
    'case',
    isActiveExpression,
    buildTrailheadSelectedIconImage(variantExpressions, pinStyle),
    buildVariantExpression(variantExpressions, variant => buildClusterImageExpression(pinStyle, variant, trailCountExpression))
  ];
}

/** Collapsed pins: a dot badged with the trailhead's state. */
export function buildTrailheadCollapsedIconImage(variantExpressions: TrailheadVariantExpressions): ExpressionSpecification {
  return buildVariantExpression(variantExpressions, variant => collapsedImageNames[variant]);
}

/** Selected or hovered pins, collapsed or not, share one icon and never show a count. */
export function buildTrailheadSelectedIconImage(
  variantExpressions: TrailheadVariantExpressions,
  pinStyle: TrailheadPinStyle = defaultPinStyle
): ExpressionSpecification {
  return buildVariantExpression(variantExpressions, variant => imageName(pinStyle, selectedImageNames[variant]));
}

/**
 * `text-field` is typed as a string, so the count has to be converted rather than passed as the
 * number the count expression resolves to. Single-trail pins drop the count, and so do hovered or
 * selected pins, whose name is written by the name layer.
 */
export function buildTrailheadCountTextField(
  isActiveExpression: ExpressionSpecification,
  trailCountExpression: ExpressionSpecification
): ExpressionSpecification {
  return ['case', isActiveExpression, '', ['==', trailCountExpression, 1], '', ['to-string', trailCountExpression]];
}

/**
 * The name layer's `text-field`. From `alwaysShowFromZoom` up, every expanded pin writes its name.
 * Below it, only the hovered or selected pin does. An empty string draws nothing.
 */
export function buildTrailheadNameTextField(
  isActiveExpression: ExpressionSpecification,
  isCollapsedExpression: ExpressionSpecification,
  alwaysShowFromZoom: number
): ExpressionSpecification {
  const name: ExpressionSpecification = ['get', 'name'];
  return [
    'step',
    ['zoom'],
    ['case', isActiveExpression, name, ''],
    alwaysShowFromZoom,
    ['case', ['any', isActiveExpression, ['!', isCollapsedExpression]], name, '']
  ];
}

// The trailhead name sits below the pin: this is the offset from the selected icon, which draws at an icon offset of [0, -16].
const selectedNameTextOffset: [number, number] = [-6 / FONT_SIZE_100, 1.4];

/**
 * `text-offset` is used to horizontally and vertically offset the text from the icon.
 * Vertical offset is constant, but horizontal offset is dynamic based on whether the in is closed, completed, saved, or default.
 */
export function buildTrailheadTextOffset(
  variantExpressions: TrailheadVariantExpressions,
  isActiveExpression: ExpressionSpecification
): ExpressionSpecification {
  return [
    'case',
    isActiveExpression,
    // Show trailhead name below pin when hovered or selected
    selectedNameTextOffset,
    variantExpressions.isClosedExpression,
    [-6 / FONT_SIZE_100, 2.5 / FONT_SIZE_100],
    variantExpressions.isCompletedExpression,
    [-6 / FONT_SIZE_100, 2.5 / FONT_SIZE_100],
    variantExpressions.isSavedExpression,
    [-6 / FONT_SIZE_100, 2.5 / FONT_SIZE_100],
    [-0.5 / FONT_SIZE_100, 2.5 / FONT_SIZE_100]
  ];
}

// Extra room, in pixels, between the bottom of a pin with a count banner and its name.
const countBannerNameSpacing = 4;

/**
 * The name layer's `text-offset`. It's the same whether or not the pin is hovered or selected so the name
 * never moves on hover. Expanded pins draw their icon at an icon offset of [0, -14], 2px lower than the
 * selected icon's [0, -16], so the name sits 2px below the selected pin's own name offset to clear the
 * resting icon. Pins with a count banner reach further down than the single-trail circle, so their name
 * gets a few more pixels.
 */
export function buildTrailheadNameTextOffset(trailCountExpression: ExpressionSpecification): ExpressionSpecification {
  const [x, y] = selectedNameTextOffset;
  const restingY = y + 2 / FONT_SIZE_100;
  return ['case', ['==', trailCountExpression, 1], [x, restingY], [x, restingY + countBannerNameSpacing / FONT_SIZE_100]];
}

/**
 * `icon-offset` is used to horizontally and vertically offset the icon from its anchor.
 * Horizontal offset is always 0, but vertical offset is dynamic based on whether the trailhead is active and or collapsed.
 * We have to vertically offset the icon because the different icons have different heights.
 */
export function buildTrailheadIconOffset(isActiveExpression: ExpressionSpecification): ExpressionSpecification {
  return ['case', isActiveExpression, [0, -16], [0, trailheadIconVerticalOffset]];
}

export function buildTrailheadIconCollapsedOffset(isActiveExpression: ExpressionSpecification): ExpressionSpecification {
  return ['case', isActiveExpression, [0, -16], [0, 0]];
}

// Wide enough for every trailhead id to land in its own slot beneath a one-point popularity difference.
const sortKeyTieBreakerRange = 1_000_000;
// 1,000 leaves room for popularity scores well past the 0–100 range, so a state band always clears it.
const popularitySortKeyLimit = 1_000;
const stateSortKeySlot = popularitySortKeyLimit * sortKeyTieBreakerRange;
// Same order as the trail pins: closed, then saved, then completed, then a plain pin.
const completedTrailheadSortKey = stateSortKeySlot;
const savedTrailheadSortKey = stateSortKeySlot * 2;
const closedTrailheadSortKey = stateSortKeySlot * 3;
// Above every state, then the hovered or selected pin above a primary one.
const primaryTrailheadSortKey = stateSortKeySlot * 4;
export const activeTrailheadSortKey = stateSortKeySlot * 8;

/**
 * Mapbox draws one icon pass and then one label pass per distinct `symbol-sort-key`, so pins that
 * tie draw every icon first and every label after, and a label lands on the neighbor's pill. Two
 * neighbors tie whenever they share their only trail, since the same trail can be listed at its
 * primary and secondary trailhead. The feature id fills the digits below popularity so every pin
 * gets its own key and draws its icon and label together.
 *
 * A trailhead whose tile feature has `primary` true draws above every trailhead that does not.
 * Within that, the pin state follows the trail pins: closed above saved, saved above completed,
 * and completed above a plain pin, so a saved collapsed dot is not covered by a default one.
 * Popularity orders the pins inside each of those groups.
 */
export function buildTrailheadSortKey(
  isActiveExpression: ExpressionSpecification,
  popularityExpression: ExpressionSpecification | number,
  { isClosedExpression, isCompletedExpression, isSavedExpression }: TrailheadVariantExpressions = {
    isClosedExpression: ['literal', false],
    isCompletedExpression: ['literal', false],
    isSavedExpression: ['literal', false]
  }
): ExpressionSpecification {
  const isPrimaryExpression: ExpressionSpecification = ['==', ['get', 'primary'], true];
  return [
    '+',
    ['case', isActiveExpression, activeTrailheadSortKey, 0],
    ['case', isPrimaryExpression, primaryTrailheadSortKey, 0],
    [
      'case',
      isClosedExpression,
      closedTrailheadSortKey,
      isSavedExpression,
      savedTrailheadSortKey,
      isCompletedExpression,
      completedTrailheadSortKey,
      0
    ],
    ['*', popularityExpression, sortKeyTieBreakerRange],
    ['%', ['to-number', ['id'], 0], sortKeyTieBreakerRange]
  ];
}
